/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isTargetInteractiveInput } from '@opengeoweb/shared';
import { WorkspaceMenuButton } from './WorkspaceMenuButton';
import { useResetWorkspaceFiltersOnRoleChange } from '../../hooks/useResetWorkspaceFiltersOnRoleChange';
import type { AppStore } from '../../store/types';
import { hasWorkspaceChanges } from '../../store/workspace/selectors';
import { workspaceListSelectors } from '../../store/workspaceList';
import { workspaceListActions } from '../../store/workspaceList/reducer';
import { useWorkspaceTranslation } from '../../utils/i18n';
import { getPresetTitle, setDocumentTitle } from '../../utils/utils';

export interface WorkspaceMenuButtonConnectProps {
  title: string;
  workspaceId: string;
  activeTab?: boolean;
  appTitle?: string;
}

export const WorkspaceMenuButtonConnect: React.FC<
  WorkspaceMenuButtonConnectProps
> = ({
  title,
  workspaceId,
  activeTab,
  appTitle,
}: WorkspaceMenuButtonConnectProps) => {
  const dispatch = useDispatch();
  const { t } = useWorkspaceTranslation();
  const hasChanges = useSelector((store: AppStore) =>
    hasWorkspaceChanges(store),
  );

  const isWorkspaceListDialogOpen = useSelector((store: AppStore) =>
    workspaceListSelectors.getIsWorkspaceListDialogOpen(store),
  );

  const setListDialogOpen = React.useCallback(
    (isListDialogOpen: boolean): void => {
      dispatch(
        workspaceListActions.toggleWorkspaceDialog({
          isWorkspaceListDialogOpen: isListDialogOpen,
        }),
      );
    },
    [dispatch],
  );

  useResetWorkspaceFiltersOnRoleChange();

  React.useEffect(() => {
    if (isWorkspaceListDialogOpen) {
      return undefined;
    }
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (
        event.code !== 'KeyW' ||
        event.ctrlKey ||
        event.metaKey ||
        event.altKey ||
        event.repeat ||
        event.defaultPrevented ||
        isTargetInteractiveInput(event.target as Element | null)
      ) {
        return;
      }
      event.preventDefault();
      setListDialogOpen(true);
    };
    document.addEventListener('keydown', handleKeyDown);
    return (): void => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isWorkspaceListDialogOpen, setListDialogOpen]);

  const isNew = workspaceId === undefined || workspaceId === '';

  const fullTitle = getPresetTitle(title, hasChanges, isNew, t);

  React.useEffect(() => {
    setDocumentTitle(fullTitle, appTitle);
  }, [fullTitle, appTitle]);

  return (
    <WorkspaceMenuButton
      activeTab={activeTab}
      hasChanges={hasChanges}
      onToggleDialog={setListDialogOpen}
      showMenu={isWorkspaceListDialogOpen}
      title={fullTitle}
    />
  );
};
